import { useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ProgressBar, Spinner } from '@/components/ui'
import type { AIGenerateEvent } from '@/hooks/useAIGenerate'
import type { Phase } from '@/hooks/useSSEGenerate'
import styles from './GenerationProgress.module.css'

const blurReveal = {
  initial: { filter: 'blur(20px)', scale: 0.95, opacity: 0 },
  animate: { filter: 'blur(0px)', scale: 1, opacity: 1 },
  transition: { type: 'spring' as const, stiffness: 200, damping: 20 },
}

interface GenerationProgressProps {
  phase: Phase
  events: AIGenerateEvent[]
  error: string | null
  totalImages?: number
}

export function GenerationProgress({ phase, events, error, totalImages = 1 }: GenerationProgressProps) {
  const statusMessage = useMemo(() => {
    const statusEvents = events.filter((e) => e.type === 'status')
    return statusEvents.length > 0
      ? (statusEvents[statusEvents.length - 1] as { type: 'status'; message: string }).message
      : 'AI 사진 생성을 준비하고 있어요...'
  }, [events])

  const imageEvents = useMemo(
    () => events.filter((e) => e.type === 'image') as Array<{
      type: 'image'; index: number; url: string; progress: number; total: number
    }>,
    [events],
  )

  const doneEvent = useMemo(
    () => events.find((e) => e.type === 'done') as
      | { type: 'done'; generatedUrls: string[]; remainingCredits?: number; failureCount?: number; creditsUsed?: number }
      | undefined,
    [events],
  )

  const progress = imageEvents.length > 0
    ? (imageEvents[imageEvents.length - 1].progress / imageEvents[imageEvents.length - 1].total) * 100
    : 0

  if (phase === 'idle') return null

  return (
    <div className={styles.container}>
      <AnimatePresence mode="wait">
        <motion.p
          key={statusMessage}
          className={styles.statusMessage}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          transition={{ duration: 0.2 }}
        >
          {phase === 'done'
            ? doneEvent?.failureCount && doneEvent.failureCount > 0
              ? doneEvent.generatedUrls.length > 0
                ? `${doneEvent.generatedUrls.length}장 완료, ${doneEvent.failureCount}장은 생성에 실패했어요`
                : '생성에 실패했어요. 다시 시도해 주세요'
              : '생성이 완료되었어요!'
            : statusMessage}
        </motion.p>
      </AnimatePresence>

      {(phase === 'generating' || phase === 'done') && (
        <div className={styles.imageGrid}>
          {Array.from({ length: totalImages }, (_, i) => {
            const imageEvent = imageEvents.find((e) => e.index === i)
            const doneUrl = doneEvent?.generatedUrls?.[i]
            const url = imageEvent?.url ?? doneUrl

            if (url) {
              return (
                <motion.div
                  key={url}
                  className={styles.imageWrapper}
                  initial={phase === 'done' && imageEvent ? false : blurReveal.initial}
                  animate={blurReveal.animate}
                  transition={blurReveal.transition}
                >
                  <img src={url} alt={`생성 결과 ${i + 1}`} className={styles.generatedImage} />
                </motion.div>
              )
            }

            if (phase === 'done') {
              return (
                <div key={`fail-${i}`} className={styles.imageWrapper}>
                  <div className={styles.failedSlot}>
                    <span className={styles.failedSlotIcon}>✕</span>
                    <span className={styles.failedSlotLabel}>실패</span>
                  </div>
                </div>
              )
            }

            return (
              <div key={`ph-${i}`} className={styles.imageWrapper}>
                <div className={styles.placeholder}>
                  <div className={styles.placeholderContent}>
                    <Spinner size="sm" />
                    <span className={styles.placeholderLabel}>생성 중</span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      )}

      {phase === 'done' && doneEvent != null && doneEvent.failureCount != null && doneEvent.failureCount > 0 && doneEvent.generatedUrls.length > 0 && (
        <p className={styles.resultInfo}>
          실패한 {doneEvent.failureCount}장의 크레딧이 환불되었어요
        </p>
      )}

      {phase === 'generating' && (
        <div className={styles.progressWrapper}>
          <ProgressBar value={progress} animated color="primary" />
          <span className={styles.progressLabel}>
            {imageEvents.length > 0
              ? `${imageEvents[imageEvents.length - 1].progress} / ${imageEvents[imageEvents.length - 1].total} 완료`
              : '생성 중...'}
          </span>
        </div>
      )}

      {phase === 'error' && error && (
        <div className={styles.errorBox}>{error}</div>
      )}
    </div>
  )
}
